import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Scissors, Shirt } from 'lucide-react';
import { fetchInvoiceTimeline, updateItemTrial, updateItemReached } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import Spinner from '../ui/Spinner';
import ChipPicker from '../ui/ChipPicker';
import StageControl from './StageControl';

/**
 * The work on one order, opened in place under its row in the order list.
 *
 * Answers the one question the list cannot: who is actually making this, and
 * how far have they got. Same facts as the order screen's timeline, read from
 * the same endpoint — this is the short version, so nobody has to open the
 * order to see where the work stands.
 */

const WORK_ICON = { CUTTING: Scissors, STITCHING: Shirt };
const WORK_LABEL = { CUTTING: 'Cutting', STITCHING: 'Stitching' };

const STATUS_TONE = {
  ASSIGNED: 'badge-neutral',
  IN_PROGRESS: 'badge-info',
  COMPLETED: 'badge-success',
};
const STATUS_LABEL = {
  ASSIGNED: 'To start',
  IN_PROGRESS: 'Working',
  COMPLETED: 'Done',
};

// '2026-10-13T14:05:00Z' -> '13-10-2026 14:05'
const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * One garment arriving at the store.
 *
 * A garment makes the journey twice: once so the customer can try it on, and
 * again — after any alteration — to be collected. `leg` says which.
 */
function ItemAtStore({ itemId, name, leg, done, onDone }) {
  const { success, error } = useToast();
  const mut = useMutation({
    mutationFn: (next) => updateItemReached(itemId, leg, next),
    onSuccess: (d) => { success(d.message || 'Saved.'); onDone(); },
    onError: (e) => error(e.response?.data?.message || 'Could not record this.'),
  });
  const what = leg === 'trial' ? 'for the trial' : 'for collection';
  return (
    <ChipPicker
      busy={mut.isPending}
      header={leg === 'trial' ? 'At the store for the trial' : 'Back at the store to collect'}
      title={`${name} — ${done ? `at the store ${what}` : `not at the store ${what} yet`}`}
      ariaLabel={`At the store ${what}: ${name}`}
      options={[
        { value: 'yes', label: 'At the store', hint: what, dot: 'bg-success-500', selected: done },
        { value: 'no', label: 'Not yet', hint: 'still at the workshop', dot: 'bg-content-subtle', selected: !done },
      ]}
      onPick={(v) => mut.mutate(v === 'yes')}
    >
      {/* The column heading says which journey, so the chip only has to say
          whether it has happened. */}
      <span className={done ? 'badge-success' : 'badge-neutral'}>
        {done ? 'At store' : 'Not yet'}
      </span>
    </ChipPicker>
  );
}

/** One garment's trial tick — the customer tries them on one at a time. */
function ItemTrial({ itemId, name, done, trialDate, onDone }) {
  const { success, error } = useToast();
  const mut = useMutation({
    mutationFn: (next) => updateItemTrial(itemId, next),
    onSuccess: (d) => { success(d.message || 'Saved.'); onDone(); },
    onError: (e) => error(e.response?.data?.message || 'Could not record the trial.'),
  });

  // No appointment booked means there is nothing to tick on any garment.
  if (!trialDate) return <span className="text-2xs text-content-subtle">—</span>;

  return (
    <ChipPicker
      busy={mut.isPending}
      header={`Trial · ${trialDate}`}
      title={`${name} — ${done ? 'tried on' : 'not tried on yet'}`}
      ariaLabel={`Trial for ${name}`}
      options={[
        { value: 'yes', label: 'Tried on', hint: 'the customer wore it', dot: 'bg-success-500', selected: done },
        { value: 'no', label: 'Not yet', dot: 'bg-warning-500', selected: !done },
      ]}
      onPick={(v) => mut.mutate(v === 'yes')}
    >
      <span className={done ? 'badge-success' : 'badge-warning'}>
        {done ? 'Done' : 'Pending'}
      </span>
    </ChipPicker>
  );
}

export default function OrderWorkPanel({ invoiceId }) {
  const qc = useQueryClient();
  // Anything changed here moves the counts in the row above it too.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['invoice-timeline', invoiceId] });
    qc.invalidateQueries({ queryKey: ['invoices'] });
    qc.invalidateQueries({ queryKey: ['production-board'] });
  };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice-timeline', invoiceId],
    queryFn: () => fetchInvoiceTimeline(invoiceId),
    staleTime: 30_000,
  });

  if (isLoading) return <div className="px-4 py-6"><Spinner label="Loading the work…" /></div>;
  if (isError) return <p className="px-4 py-5 text-sm text-content-muted">Could not load the work on this order.</p>;

  const jobs = data?.jobs || [];
  const garments = data?.garments || [];


  // Grouped by garment, because that is how the shop thinks about it: this
  // shirt, who cut it, who stitched it.
  const byItem = jobs.reduce((acc, j) => {
    (acc[j.itemName] ||= []).push(j);
    return acc;
  }, {});

  return (
    <div className="px-3 py-3 sm:px-4">
      {/* An order with nobody on it still has garments to move along, so the
          controls below stay — only this table steps aside. */}
      {!jobs.length ? (
        <p className="rounded-lg border border-dashed border-line-strong py-4 text-center text-sm italic text-content-subtle">
          Nobody has been given work on this order yet.
        </p>
      ) : (
      <>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <h4 className="text-2xs font-bold uppercase tracking-wider text-content-subtle">Who is making this</h4>
        <span className="text-2xs text-content-subtle">
          {jobs.length} job{jobs.length === 1 ? '' : 's'}
          {data?.daysOpen != null && ` · ${data.daysOpen} day${data.daysOpen === 1 ? '' : 's'} since booking`}
        </span>
      </div>

      {/* Capped, not stretched: six short columns spread over a whole table
         width leave more gap than content. `min-w` keeps it scrollable on a
         phone; `max-w` stops it sprawling on a desk monitor. */}
      <div className="max-w-4xl overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="w-full min-w-[34rem] text-sm">
          <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
            <tr>
              <th className="w-40 px-2.5 py-1.5">Garment</th>
              <th className="w-24 px-2.5 py-1.5">Work</th>
              <th className="w-32 px-2.5 py-1.5">Worker</th>
              <th className="w-32 px-2.5 py-1.5">Started</th>
              <th className="w-32 px-2.5 py-1.5">Finished</th>
              <th className="w-20 px-2.5 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byItem).map(([itemName, list]) => list.map((j, i) => {
              const Icon = WORK_ICON[j.workType];
              return (
                <tr key={`${itemName}-${j.workType}-${j.pieceNo || i}`} className="border-t border-line">
                  <td className="max-w-[10rem] truncate px-2.5 py-1.5 font-medium text-content">
                    {/* The garment is named once per group, so the eye follows
                        the rows that belong together. */}
                    {i === 0 ? itemName : <span className="text-content-subtle">·</span>}
                    {j.pieceNo ? <span className="ml-1 text-2xs text-content-subtle">#{j.pieceNo}</span> : null}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1.5 text-content-muted">
                    <span className="inline-flex items-center gap-1.5">
                      {Icon && <Icon size={13} className="text-content-subtle" />}
                      {WORK_LABEL[j.workType] || j.workType}
                    </span>
                  </td>
                  <td className="max-w-[8rem] truncate px-2.5 py-1.5">
                    {j.worker || <span className="text-content-subtle">Not assigned</span>}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1.5 text-xs text-content-muted">
                    {when(j.startedAt) || <span className="text-content-subtle">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1.5 text-xs text-content-muted">
                    {when(j.completedAt) || <span className="text-content-subtle">—</span>}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span className={STATUS_TONE[j.status] || 'badge-neutral'}>
                      {STATUS_LABEL[j.status] || j.status}
                    </span>
                  </td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Per garment: where it is, and the two things a manager changes —
          its stage and whether the customer has tried it on. Same controls
          here as on the production board, so there is one way to do it. */}
      {garments.length > 0 && (
        <div className="mt-3">
          <h4 className="mb-2 text-2xs font-bold uppercase tracking-wider text-content-subtle">
            Each garment
          </h4>
          {/* A table, not a row of chips: with four controls per garment the
              only way to know what each one means is a heading above it. The
              chips can then say less — "At store" under a column headed
              "At store (trial)" needs no more words. */}
          <div className="max-w-4xl overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
                <tr>
                  <th className="w-40 px-2.5 py-1.5">Garment</th>
                  <th className="w-44 px-2.5 py-1.5">Stage</th>
                  <th className="w-32 px-2.5 py-1.5">At store (trial)</th>
                  <th className="w-28 px-2.5 py-1.5">Trial</th>
                  <th className="w-32 px-2.5 py-1.5">At store (delivery)</th>
                </tr>
              </thead>
              <tbody>
                {garments.map((g) => (
                  <tr key={g.itemId} className="border-t border-line align-middle">
                    <td className="max-w-[10rem] px-2.5 py-1.5">
                      <span className="block truncate font-medium text-content">
                        {g.itemName}
                        {g.qty > 1 && <span className="font-normal text-content-subtle"> ×{g.qty}</span>}
                      </span>
                      {g.holder && (
                        <span className="block truncate text-2xs text-content-subtle">with {g.holder}</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <StageControl
                        item={{ id: g.itemId, stage: g.stage, expectedAt: g.expectedAt }}
                        invoiceId={invoiceId}
                        compact
                        onMoved={refresh}
                      />
                    </td>
                    {/* The garment's two journeys to the store, in the order
                        they happen: arrives for the trial, is tried on, comes
                        back to be collected. */}
                    <td className="px-2.5 py-1.5">
                      <ItemAtStore
                        itemId={g.itemId}
                        name={g.itemName}
                        leg="trial"
                        done={g.reachedForTrial}
                        onDone={refresh}
                      />
                    </td>
                    <td className="px-2.5 py-1.5">
                      <ItemTrial
                        itemId={g.itemId}
                        name={g.itemName}
                        done={g.trialDone}
                        trialDate={data?.trialDate}
                        onDone={refresh}
                      />
                    </td>
                    <td className="px-2.5 py-1.5">
                      <ItemAtStore
                        itemId={g.itemId}
                        name={g.itemName}
                        leg="delivery"
                        done={g.reachedForDelivery}
                        onDone={refresh}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
